import React, { useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import {
  GrayDownArrowIcon,
  GrayUpArrowIcon,
  RequestReasonIcon,
} from 'src/assets/svgs'
import { ModalProps, asModal } from 'src/components/hoc/asModal'
import { SmallButton } from 'src/elements/Button'
import { PremiumContribution } from 'src/models/PremiumContribution'
import {
  _contributionPremiumBorder,
  _reasongreen,
  _reasonoverlay,
  trueblack,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled, { css } from 'styled-components'
import {
  PremiumContributionDate,
  PremiumContributionTop,
  UserInfo,
} from './PremiumContributions'

const CONTENT_LIMIT = 126 // コンテンツを切り取る長さ

type PremiumContributionModalProps = {
  premiumContribution: PremiumContribution
  onSubmitPostInterested: (
    companyId: number,
    reason: string,
    detail: string,
    premiumContributionId: number,
  ) => void
  hasPaid: boolean
}
const PremiumContributionModalContent: React.FC<
  ModalProps<PremiumContributionModalProps>
> = ({ close, premiumContribution, onSubmitPostInterested, hasPaid }) => {
  const [showContent, setShowContent] = useState<boolean>(false)
  const [detail, setDetail] = useState('')

  const reason = 'talk_about_contribution' // 80: 投稿について話をしたい
  const displayReason = '投稿について話をしたい'

  const submit = () => {
    if (!detail) {
      window.alert('詳細を入力してください')
      return
    }
    onSubmitPostInterested(
      premiumContribution.companyId,
      reason,
      detail,
      premiumContribution.id,
    )
  }

  return (
    <Layout>
      <Content>
        <Head>
          <HeadTitle>
            <HeadTitleIcon>
              <RequestReasonIcon />
            </HeadTitleIcon>
            <HeadTitleText>メッセージを制作してください</HeadTitleText>
          </HeadTitle>
          <HeadDescription>
            決裁者様にメッセージを送信して、つながりを作りましょう。
          </HeadDescription>
        </Head>
        <ContentWrapper>
          <PremiumContributionDate premiumContribution={premiumContribution} />
          <Spacer />
          <PremiumContributionTop
            premiumContribution={premiumContribution}
            hasPaid={hasPaid}
            shouldcollapse={false}
          />
          <UserInfoWrapper>
            <UserInfo
              isModal={true}
              premiumContribution={premiumContribution}
            />
          </UserInfoWrapper>
          <PremiumContributionContent>
            {premiumContribution.content.length > CONTENT_LIMIT ? (
              <>
                {!showContent && (
                  <>
                    <div>
                      {premiumContribution.content.slice(0, CONTENT_LIMIT)}...
                    </div>
                    <PremiumContributionContentButton
                      onClick={() => setShowContent(true)}
                    >
                      <GrayDownArrowIcon />
                      <PremiumContributionContentSpan>
                        もっと見る
                      </PremiumContributionContentSpan>
                    </PremiumContributionContentButton>
                  </>
                )}
                {showContent && (
                  <>
                    <div>{premiumContribution.content}</div>

                    <PremiumContributionContentButton
                      onClick={() => setShowContent(false)}
                    >
                      <GrayUpArrowIcon />
                      <PremiumContributionContentSpan>
                        閉じる
                      </PremiumContributionContentSpan>
                    </PremiumContributionContentButton>
                  </>
                )}
              </>
            ) : (
              <div>{premiumContribution.content}</div>
            )}
          </PremiumContributionContent>
        </ContentWrapper>
        <ReasonWrapper>
          <ReasonLabel>興味がある理由</ReasonLabel>
          <ReasonItems>
            <ReasonItem>
              <input
                type={'radio'}
                name="reason"
                value={reason}
                defaultChecked={true}
              />
              <span>{displayReason}</span>
              <div />
            </ReasonItem>
          </ReasonItems>
          <DetailFormDiv>
            <ReasonLabel>詳細をご記入ください</ReasonLabel>
            <DetailTextAreaDiv>
              <TextareaAutosize
                value={detail}
                minRows={4}
                maxRows={4}
                onChange={({ target: { value } }) => setDetail(value)}
              />
            </DetailTextAreaDiv>
          </DetailFormDiv>
        </ReasonWrapper>
        <Actions>
          <Action onClick={close} cancel={true}>
            キャンセル
          </Action>
          <Action onClick={() => submit()}>送信する</Action>
        </Actions>
      </Content>
    </Layout>
  )
}

const modalOverlayStyle = css`
  backdrop-filter: blur(6px) brightness(0.5);
  background-color: ${_reasonoverlay}80;
`

export const PremiumContributionModal = asModal<PremiumContributionModalProps>(
  PremiumContributionModalContent,
  {
    overlayStyle: modalOverlayStyle,
    scrollable: true,
  },
)

const Layout = styled.div`
  width: 692px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  border-radius: 25px;
  box-shadow: 0 5px 15px ${trueblack}80;
`

const Content = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px;
`

const Head = styled.div``

const HeadTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 8px;
`

const HeadTitleIcon = styled.div`
  margin-right: 8px;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const HeadTitleText = styled.p`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
`

const HeadDescription = styled.span`
  padding-top: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
`

const ContentWrapper = styled.div`
  margin-top: 16px;
  border-top: 1px solid ${_contributionPremiumBorder};
  padding-top: 16px;
`
const Spacer = styled.div`
  padding-top: 16px;
`
const UserInfoWrapper = styled.div`
  padding-top: 12px;
  display: flex;
  justify-content: flex-start;
`
const PremiumContributionContent = styled.div`
  line-height: 21px;
  white-space: pre-wrap;
  padding-bottom: 20px;
  border-bottom: 1px dashed ${_contributionPremiumBorder};
`
const PremiumContributionContentButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${fontWeightBold};
  padding: 16px 0px;
  cursor: pointer;
`
const PremiumContributionContentSpan = styled.span`
  margin-left: 8px;
`

const ReasonWrapper = styled.div`
  padding: 24px 0px;
`

const ReasonLabel = styled.span`
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #888;
  margin-bottom: 8px;
`

const ReasonItems = styled.div`
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`

const ReasonItemCss = css`
  margin: 6px 0;
  display: inline-flex;
  align-items: center;
  margin-right: 6px;
  padding: 6px 16px;
  border-radius: 100px;
  cursor: pointer;
  position: relative;

  > input {
    display: none;
  }

  > span {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
    color: ${_reasongreen};
  }

  > div {
    z-index: 0;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 100px;
    border: solid 1px #ccc;
  }
  input:checked + span {
    position: relative;
    z-index: 1;
    color: #fff;
  }
  input:checked + span + div {
    border: solid 1px #02b7b0;
    background: #02b7b0;
  }
`

const ReasonItem = styled.label`
  ${ReasonItemCss}
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const Action = styled(SmallButton)<{ cancel?: boolean }>`
  ${({ cancel }) =>
    cancel
      ? css`
          color: ${_reasongreen};
          background: ${truewhite};
        `
      : css`
          color: ${truewhite};
          background: ${_reasongreen};
        `}

  margin-left: 16px;
`

const DetailFormDiv = styled.div`
  margin-top: 14px;
`

const DetailTextAreaDiv = styled.div`
  border: 1px solid #cdcdcd;
  border-radius: 5px;

  background-color: #ffffffc0;
  backdrop-filter: blur(50px) brightness(1.75);
  padding: 14px;
  text-align: left;

  textarea {
    width: 100%;
    resize: none;
    font-size: 14px;
    letter-spacing: 0.036em;
    line-height: 1.5;
    overflow-y: auto;
    cursor: auto;
  }
`
